/**
 * Règles d'alerte : destinataires, délai de relance, escalade, condition de clôture
 * (`10-parametrage-administration.md`). Le serveur exige au moins un destinataire pour qu'une règle
 * puisse notifier qui que ce soit — l'écran ne le vérifie qu'en confort, la validation de fond reste
 * côté serveur.
 */
import { useState } from "react";

import { ErreurApi } from "../../shared/api/erreurs";
import { Chargement, Erreur } from "../../shared/composants/Etats";
import { useModifierRegleAlerte, useReglesAlerte } from "./requetes";

const LIBELLE_TYPE: Record<string, string> = {
  batterie_faible: "Batterie faible",
  tracker_silencieux: "Tracker silencieux",
  sortie_de_zone: "Sortie de zone",
  duree_anormale_escale: "Durée anormale sur escale",
  anomalie_de_suivi: "Anomalie de suivi",
  epreuve_etancheite_rappel: "Rappel d'épreuve d'étanchéité",
  epreuve_etancheite_retard: "Épreuve d'étanchéité en retard",
  document_echeance: "Document arrivant à échéance",
};

export function EcranReglesAlerte() {
  const requete = useReglesAlerte();
  const modifier = useModifierRegleAlerte();
  const [enEdition, definirEnEdition] = useState<string | null>(null);
  const [destinataires, definirDestinataires] = useState("");

  const regles = requete.data?.data ?? {};
  const types = Object.keys(regles).sort();

  async function soumettre(type: string, evenement: React.FormEvent) {
    evenement.preventDefault();
    const regle = regles[type]!;
    await modifier.mutateAsync({
      type,
      corps: {
        active: regle.active,
        cloture_automatique: regle.cloture_automatique,
        destinataires: destinataires
          .split(",")
          .map((e) => e.trim())
          .filter(Boolean),
        destinataires_escalade: regle.destinataires_escalade,
      },
    });
    definirEnEdition(null);
  }

  if (requete.isPending) return <Chargement libelle="Chargement des règles d'alerte…" />;
  if (requete.isError) return <Erreur erreur={requete.error} reessayer={() => void requete.refetch()} />;

  return (
    <section>
      <h1>Règles d'alerte</h1>
      <table className="tableau">
        <thead>
          <tr>
            <th scope="col">Type</th>
            <th scope="col">Active</th>
            <th scope="col">Destinataires</th>
            <th scope="col">Clôture automatique</th>
            <th scope="col"></th>
          </tr>
        </thead>
        <tbody>
          {types.map((type) => {
            const regle = regles[type]!;
            return (
              <tr key={type}>
                <td>{LIBELLE_TYPE[type] ?? regle.libelle}</td>
                <td>{regle.active ? "oui" : "non"}</td>
                <td>
                  {enEdition === type ? (
                    <form className="formulaire-ligne" onSubmit={(e) => void soumettre(type, e)}>
                      <input
                        aria-label={`Destinataires pour ${regle.libelle}`}
                        placeholder="adresses séparées par des virgules"
                        value={destinataires}
                        onChange={(e) => definirDestinataires(e.target.value)}
                      />
                      <button type="submit" disabled={modifier.isPending}>
                        Enregistrer
                      </button>
                      <button type="button" onClick={() => definirEnEdition(null)}>
                        Annuler
                      </button>
                    </form>
                  ) : regle.destinataires.length > 0 ? (
                    regle.destinataires.join(", ")
                  ) : (
                    <span className="etat--vide">aucun</span>
                  )}
                </td>
                <td>{regle.cloture_automatique ? "oui" : "non"}</td>
                <td>
                  {enEdition !== type && (
                    <button
                      type="button"
                      onClick={() => {
                        definirEnEdition(type);
                        definirDestinataires(regle.destinataires.join(", "));
                      }}
                    >
                      Modifier
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {modifier.isError && (
        <p role="alert">
          {modifier.error instanceof ErreurApi ? modifier.error.message : "La modification a échoué."}
        </p>
      )}
    </section>
  );
}
